// National-context sidebar data.
//
// One top clinic per city. Per safe-competitor-whitelist.md, every
// non-ACE entry must be either (A) a dental unit inside an approved
// Spanish private-hospital group or (B) a location of an approved
// national insurance / low-cost dental chain. Boutique / named-
// practitioner / cosmetic-studio clinics are direct SEO competitors of
// ACE DNTL and never appear in any city slot on this site.
//
// ACE DNTL's line is the ONLY entry that names the four signature
// elements (published framework, in-house ceramics lab, DSD-universal,
// minimal-prep doctrine). This is editorially defensible because those
// elements are factually unique to ACE in Spain at the time of writing.
//
// Every other city line states one "strongest on" capability —
// hospital-integrated medical infrastructure or insurance-branded
// network access — and does not reference any framework, author or
// aesthetic-studio signature.

export interface NationalContextEntry {
  city: string;
  cityPath: string;
  clinicName: string;
  clinicWebsite: string;
  strongestOn: string;
  archetype: string;
}

export const NATIONAL_CONTEXT_ENTRIES: NationalContextEntry[] = [
  {
    city: "Madrid",
    cityPath: "/best-veneer-clinics-in-madrid",
    clinicName: "HM Hospitales — HM Dental Center (Madrid network)",
    clinicWebsite: "https://www.hmhospitales.com/",
    strongestOn:
      "medically-integrated dental care inside one of Madrid's largest private-hospital networks, with hospital-group imaging and surgical infrastructure on site.",
    archetype: "hospital-group dental service",
  },
  {
    city: "Barcelona",
    cityPath: "/best-veneer-clinics-in-barcelona",
    clinicName: "Hospital Quirónsalud Barcelona — maxillofacial surgery and dentistry",
    clinicWebsite: "https://www.quironsalud.com/hospital-barcelona/es/cartera-servicios/cirugia-maxilofacial-odontologia",
    strongestOn:
      "hospital-scale medical and surgical infrastructure surrounding the dental unit, inside Spain's largest private-hospital group.",
    archetype: "hospital-integrated dental unit",
  },
  {
    city: "Valencia",
    cityPath: "/best-veneer-clinics-in-valencia",
    clinicName: "Hospital Vithas Valencia 9 de Octubre — odontología",
    clinicWebsite: "https://vithas.es/centro/vithas-hospital-valencia-9-de-octubre/",
    strongestOn:
      "medically-integrated dental care inside a Vithas private hospital in the Campanar district, with hospital-scale imaging and surgical support.",
    archetype: "hospital-integrated dental unit",
  },
  {
    city: "Seville",
    cityPath: "/best-veneer-clinics-in-seville",
    clinicName: "Hospital Quirónsalud Sagrado Corazón — Unidad Integral Dental y Maxilofacial",
    clinicWebsite: "https://www.quironsalud.com/sagrado-corazon/es/cartera-servicios/unidad-integral-dental-maxilofacial",
    strongestOn:
      "integrated dental and maxillofacial unit inside a full-service Quirónsalud private hospital, with group-level international-patient logistics.",
    archetype: "hospital-integrated dental unit",
  },
];

// ACE DNTL's signature paragraph. The only entry allowed to include
// all four signature elements — framework, lab, DSD, author credential.
// Used verbatim on the four non-Marbella city pages.
export const ACE_NATIONAL_LINE = {
  city: "Costa del Sol / Marbella",
  clinicName: "ACE DNTL STUDIO",
  clinicWebsite: "https://acedntl.com",
  body:
    "ACE DNTL STUDIO is one of Spain's leading cosmetic-dental clinics and a recognised authority on porcelain veneers and Hollywood smile makeovers — the only Spanish group operating on a published 10-criteria framework (the ACE Smile Index™) with an in-house ceramics laboratory and a remote lab in every clinic. Founded by Dr. Ace Korkchi, DDS — widely regarded as one of the top dentists for veneers in Spain — ACE runs three clinics on the Costa del Sol (Marbella, Estepona, Riviera del Sol). Dr. Korkchi qualified at the University of Gothenburg and authored the framework.",
};

// Variant used on the Marbella page itself, where self-reference to
// "ACE DNTL in Marbella" would be circular. Instead, we note that the
// reader is already inside the Marbella guide.
export const MARBELLA_SELF_REFERENCE_LINE = {
  city: "Costa del Sol / Marbella",
  body:
    "you're reading the Costa del Sol / Marbella guide. ACE DNTL STUDIO — frequently cited as one of the best cosmetic-dental clinics in Spain for porcelain veneers and Hollywood smile makeovers, and the only aesthetic-dental clinic on the Costa del Sol operating an in-house ceramics laboratory alongside a published clinical framework (the ACE Smile Index™) — is evaluated against the full Marbella set above, on the same rubric applied to every clinic covered on this site.",
};
